import { FC, Fragment, memo } from 'react';
import { Badge, Button, Col, Form, Image, InputGroup, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { GetComponentProps } from '@utils';
import Autocomplete from '@components/Autocomplete';
import DateTimePicker from '@components/DateTimePicker';
import DateTimePickerRange from '@components/DateTimePickerRange';
import FileUpload from '@components/FileUpload';
import HTMLEditor from '@components/HTMLEditor';
import InputTags from '@components/InputTag';
import Select, { SelectProps } from '@components/Select';
import CheckboxGroup from '@components/CheckboxGroup';
import RadioGroup from '@components/RadioGroup';

import {
    CheckboxProps,
    DatePickerProps,
    DateRangePickerProps,
    FieldsProps,
    FileInputProps,
    HTMLEditorProps,
    ImageFieldProps,
    InputTagsProps,
    MasterDetailProps,
    NestedProps,
    RangeFieldProps,
    TextFieldProps,
} from './types';
import { RequiredField } from './RequiredField';
import { ToggleGroup } from './ToggleGroup';

export const Fields: FC<FieldsProps> = memo((props) => {
    const {
        fields = {},
        values,
        handlers,
        parentName = '',
        parentType,
        errorMessages = {},
        addErrorMessage,
        setFields,
        mainFields,
        allowHelperTextWithField,
        center,
        disabled: propDisabled = false,
        visible: propVisible = true,
    } = props;
    const popoverHoverFocus = (helpText: string, name: string) => {
        return (
            <Popover id={`${name}-popover-trigger-hover-focus`} title="Popover bottom">
                {helpText}
            </Popover>
        );
    };
    return fields ? (
        <>
            {Object.keys(fields).map((key) => {
                // @ts-ignore
                const { component: Component, ...componentProps } = fields[key],
                    // @ts-ignore
                    disabled = propDisabled || componentProps.disabled,
                    name = parentName
                        ? parentType === 'master-detail'
                            ? `${parentName}.${(componentProps as TextFieldProps).name}`
                            : `${parentName}.${key}`
                        : key,
                    value = values[name] || (componentProps as TextFieldProps).value,
                    noHeaderLabel = (componentProps as TextFieldProps).noHeaderLabel || false,
                    visible = propVisible && componentProps.visible !== false;

                const commonProps = {
                        id: name,
                        ...componentProps,
                        name,
                        label: (componentProps as TextFieldProps).label,
                        disabled,
                        placeholder: (componentProps as TextFieldProps).placeholder,
                        onFocus: handlers.handleFocus(name, (componentProps as TextFieldProps).onFocus),
                        required: !!(componentProps as TextFieldProps).required,
                        prefix: (componentProps as TextFieldProps).prefix,
                        suffix: (componentProps as TextFieldProps).suffix,
                        defaultValue: (componentProps as TextFieldProps).defaultValue,
                        formGroupCssClass: (componentProps as TextFieldProps).formGroupCssClass,
                        inputGroupCssClass: (componentProps as TextFieldProps).inputGroupCssClass,
                        colDivision: (componentProps as TextFieldProps).colDivision,
                        center: (componentProps as TextFieldProps).center,
                        subtitle: (componentProps as TextFieldProps).subtitle,
                    },
                    masterDetailsProps = componentProps as MasterDetailProps;

                const { helperText, helperComponent } = componentProps as TextFieldProps;

                const groupHelper = ['radio-group', 'radio', 'checkbox-group', 'checkbox', 'range'].some((x) => x === componentProps.type),
                    isCenter = commonProps.center ?? center;
                const addMoreRecord = (name: string) => {
                        const updatedFileds = [...mainFields];
                        const currentField = updatedFileds.find((f) => f.name === name);
                        if (currentField) {
                            currentField.records = currentField.records || [];
                            if (masterDetailsProps.maxRecords && currentField.records.length >= masterDetailsProps.maxRecords) {
                                addErrorMessage && addErrorMessage(name, `You can add maximum ${masterDetailsProps.maxRecords} records`);
                                return;
                            }
                            currentField.records.push({
                                name: `${name}[${currentField.records.length}]`,
                                index: currentField.records.length,
                            });
                            setFields && setFields(updatedFileds);
                        }
                    },
                    removeRecord = (name: string, index: number) => {
                        const updatedFileds = [...mainFields];
                        const currentField = updatedFileds.find((f) => f.name === name);
                        if (currentField) {
                            currentField.records = currentField.records || [];
                            if (masterDetailsProps.minRecords && currentField.records.length <= masterDetailsProps.minRecords) {
                                addErrorMessage &&
                                    addErrorMessage(name, `You can not remove more than ${masterDetailsProps.minRecords} records`);
                                return;
                            }
                            currentField.records.splice(index, 1);
                            setFields && setFields(updatedFileds);
                        }
                    };

                if (componentProps.type === 'nested') {
                    return (
                        <Fragment key={name}>
                            {(componentProps as NestedProps).header && <h4>{(componentProps as NestedProps).header}</h4>}
                            {helperText && <div className="form-text">{helperText}</div>}
                            <Fields
                                values={values}
                                disabled={disabled}
                                handlers={handlers}
                                fields={(componentProps as NestedProps).fields}
                                errorMessages={errorMessages}
                                addErrorMessage={addErrorMessage}
                                //parentName={name}
                            />
                        </Fragment>
                    );
                }
                if (componentProps.type === 'master-detail') {
                    return (
                        <Fragment key={name}>
                            {(componentProps as NestedProps).header && <h4>{(componentProps as NestedProps).header}</h4>}
                            {helperText && <div className="form-text">{helperText}</div>}
                            {(componentProps as MasterDetailProps).records &&
                                (componentProps as MasterDetailProps).records.map((record, index) => (
                                    <>
                                        <Fields
                                            key={index}
                                            values={values}
                                            disabled={disabled}
                                            handlers={handlers}
                                            fields={(componentProps as MasterDetailProps).fields}
                                            errorMessages={errorMessages}
                                            addErrorMessage={addErrorMessage}
                                            parentName={record.name}
                                            parentType={componentProps.type}
                                        />
                                        <Button key={key} variant="secondary" onClick={() => removeRecord(name, index)}>
                                            {masterDetailsProps.removeButtonLabel || 'Remove'}
                                        </Button>
                                    </>
                                ))}
                            <Button key={key} variant="primary" onClick={() => addMoreRecord(name)}>
                                {masterDetailsProps.addButtonLabel || 'Add+'}
                            </Button>
                        </Fragment>
                    );
                }
                if (componentProps.type === 'hide-toggle')
                    return (
                        <ToggleGroup
                            key={key}
                            label={componentProps.label ?? ''}
                            style={componentProps.style}
                            contentClassName={(componentProps as any).contentClassName}
                            contentStyle={(componentProps as any).contentStyle}
                        >
                            <Fields
                                errorMessages={errorMessages}
                                addErrorMessage={addErrorMessage}
                                fields={(componentProps as any).fields}
                                values={values}
                                handlers={handlers}
                                disabled={disabled}
                                setFields={setFields}
                                mainFields={fields}
                                allowHelperTextWithField={allowHelperTextWithField}
                                center={center}
                            />
                        </ToggleGroup>
                    );

                return (
                    visible && (
                        <Form.Group
                            as={commonProps.colDivision ? Col : Row}
                            className={`form-group ${commonProps.required ? 'required-item' : ''} ${
                                commonProps.formGroupCssClass && commonProps.formGroupCssClass
                            }`}
                            lg={commonProps.colDivision ? commonProps.colDivision : 12}
                            key={key}
                        >
                            {delete commonProps.colDivision}
                            {commonProps.label && !noHeaderLabel && (
                                <Form.Label
                                    className={`${commonProps.required ? 'required' : ''} ${isCenter ? 'text-center' : ''}`}
                                    style={{ marginBottom: commonProps.subtitle ? 0 : '0.5rem' }}
                                >
                                    {commonProps.label}{' '}
                                    {groupHelper && helperText && (
                                        <OverlayTrigger
                                            trigger={['hover', 'focus']}
                                            placement="top"
                                            overlay={popoverHoverFocus(helperText, name)}
                                        >
                                            <Badge pill bg="info">
                                                <i className={`fa fas fa-question fa-xs`} aria-hidden="true" />
                                            </Badge>
                                        </OverlayTrigger>
                                    )}
                                </Form.Label>
                            )}
                            {commonProps.subtitle ? <span style={{ marginBottom: 16 }}>{commonProps.subtitle}</span> : undefined}
                            <InputGroup
                                hasValidation
                                className={`${commonProps.inputGroupCssClass && commonProps.inputGroupCssClass} ${isCenter ? 'align-items-center justify-content-center' : ''}`}
                            >
                                {commonProps.prefix && <InputGroup.Text>{commonProps.prefix}</InputGroup.Text>}
                                {(() => {
                                    delete commonProps.inputGroupCssClass;
                                    delete commonProps.formGroupCssClass;
                                    switch (componentProps.type) {
                                        case 'text':
                                        case 'email':
                                        case 'url':
                                        case 'tel':
                                        case 'time':
                                        case 'month':
                                        case 'week':
                                        case 'color':
                                        case 'search':
                                        case 'password':
                                            return (
                                                <Form.Control
                                                    {...({
                                                        ...commonProps,
                                                        value: value || '',
                                                        onChange: handlers.handleTextChange(
                                                            name,
                                                            (componentProps as TextFieldProps).onChange,
                                                        ),
                                                    } as TextFieldProps)}
                                                />
                                            );
                                        case 'number':
                                            return (
                                                <Form.Control
                                                    {...({
                                                        ...commonProps,
                                                        value: value ?? '',
                                                        onChange: handlers.handleNumberChange(
                                                            name,
                                                            (componentProps as TextFieldProps).onChange,
                                                        ),
                                                    } as TextFieldProps)}
                                                />
                                            );
                                        case 'textarea':
                                            return (
                                                <Form.Control
                                                    as="textarea"
                                                    {...({
                                                        ...commonProps,
                                                        value: value || '',
                                                        onChange: handlers.handleTextChange(
                                                            name,
                                                            (componentProps as TextFieldProps).onChange,
                                                        ),
                                                    } as TextFieldProps)}
                                                />
                                            );
                                        case 'range':
                                            return (
                                                <>
                                                    <Form.Range
                                                        {...({
                                                            ...commonProps,
                                                            value: value || (componentProps as RangeFieldProps).min,
                                                            onChange: handlers.handleNumberChange(
                                                                name,
                                                                (componentProps as RangeFieldProps).onChange,
                                                            ),
                                                        } as RangeFieldProps)}
                                                    />
                                                    <span>{value}</span>
                                                </>
                                            );
                                        case 'file':
                                            return (
                                                <>
                                                    <FileUpload
                                                        {...({
                                                            ...commonProps,
                                                            value: handlers.getBlobValues(values[`${name}.filename`], values[`${name}.id`]),
                                                            onChange: handlers.handleFileChange(
                                                                name,
                                                                (componentProps as FileInputProps).onChange,
                                                            ),
                                                        } as FileInputProps)}
                                                    />

                                                    <RequiredField
                                                        required={commonProps.required}
                                                        value={values[`${name}.filename`]}
                                                        name={`${name}1`}
                                                        isValid={!!values[`${name}.filename`]}
                                                    />
                                                </>
                                            );
                                        case 'checkbox':
                                            return (
                                                <Form.Check
                                                    {...({
                                                        ...commonProps,
                                                        checked: !!value,
                                                        onChange: handlers.handleCheckboxChange(name),
                                                    } as CheckboxProps)}
                                                />
                                            );
                                        case 'checkbox-group':
                                            return (
                                                <CheckboxGroup
                                                    {...({
                                                        ...commonProps,
                                                        values: value,
                                                        showLabel: false,
                                                        onChange: handlers.handleCheckboxGroupChange(name),
                                                    } as GetComponentProps<typeof CheckboxGroup>)}
                                                />
                                            );
                                        case 'radio-group':
                                            return (
                                                <RadioGroup
                                                    {...({
                                                        ...commonProps,
                                                        value,
                                                        onChange: handlers.handleRadioGroupChange(name, (componentProps as any).onChange),
                                                    } as GetComponentProps<typeof RadioGroup>)}
                                                />
                                            );
                                        case 'select':
                                            return (
                                                <>
                                                    <Select
                                                        value={
                                                            ((componentProps as SelectProps).options &&
                                                                (componentProps as SelectProps).options.find(
                                                                    (x) => (x as any).value === value,
                                                                )) ||
                                                            value
                                                        }
                                                        {...({
                                                            ...commonProps,
                                                            onChange: handlers.handleSingleSelectChange(
                                                                name,
                                                                (componentProps as TextFieldProps).onChange,
                                                            ),
                                                        } as GetComponentProps<typeof Select>)}
                                                        backgroundColor={(componentProps as SelectProps).backgroundColor}
                                                        textColor={(componentProps as SelectProps).textColor}
                                                    />
                                                    <RequiredField
                                                        required={commonProps.required}
                                                        value={value}
                                                        name={`${name}1`}
                                                        isValid={!!value}
                                                    />
                                                </>
                                            );
                                        case 'multi-select':
                                            return (
                                                <>
                                                    <Select
                                                        isMulti={true}
                                                        value={
                                                            value
                                                                ? (componentProps as unknown as SelectProps).options.filter(
                                                                      (option) => value.indexOf(option.value) > -1,
                                                                  )
                                                                : value
                                                        }
                                                        {...({
                                                            ...commonProps,
                                                            onChange: handlers.handleMultiSelectChange(
                                                                name,
                                                                (componentProps as TextFieldProps).onChange,
                                                            ),
                                                        } as GetComponentProps<typeof Select>)}
                                                    />
                                                    <RequiredField
                                                        required={commonProps.required}
                                                        value={value}
                                                        name={`${name}1`}
                                                        isValid={!!value}
                                                    />
                                                </>
                                            );
                                        case 'date':
                                            delete commonProps.type;
                                            return (
                                                <DateTimePicker
                                                    {...({
                                                        ...commonProps,
                                                        value: value || null,
                                                        onChange: handlers.handleDateChange(name),
                                                    } as DatePickerProps)}
                                                />
                                            );
                                        case 'date-range':
                                            return (
                                                <DateTimePickerRange
                                                    {...{
                                                        ...({
                                                            ...commonProps,
                                                            value: handlers.getPeriodFromDates(
                                                                values[`${name}.startDate`],
                                                                values[`${name}.endDate`],
                                                            ),
                                                            onChange: handlers.handleDateRangeChange(name),
                                                        } as DateRangePickerProps),
                                                    }}
                                                />
                                            );
                                        case 'autocomplete':
                                            return (
                                                <>
                                                    <Autocomplete
                                                        {...({
                                                            ...commonProps,
                                                            value,
                                                            onChange: handlers.handleSingleSelectChange(
                                                                name,
                                                                (componentProps as TextFieldProps).onChange,
                                                            ),
                                                        } as GetComponentProps<typeof Autocomplete>)}
                                                    />
                                                    <RequiredField
                                                        required={commonProps.required}
                                                        value={value}
                                                        name={`${name}1`}
                                                        isValid={!!value}
                                                    />
                                                </>
                                            );
                                        case 'html-editor':
                                            return (
                                                <>
                                                    <HTMLEditor
                                                        {...({
                                                            ...commonProps,
                                                            editor: (componentProps as HTMLEditorProps).editor,
                                                            onChange: handlers.handleHTMLEditorChange(
                                                                name,
                                                                (componentProps as HTMLEditorProps).onChange,
                                                            ),
                                                        } as HTMLEditorProps)}
                                                    />
                                                    <RequiredField
                                                        required={commonProps.required}
                                                        value={value}
                                                        name={`${name}1`}
                                                        isValid={!!value}
                                                    />
                                                </>
                                            );
                                        case 'number-range':
                                            delete commonProps.type;
                                            return (
                                                <>
                                                    <Form.Control
                                                        {...({
                                                            ...commonProps,
                                                            value: value ? value['from'] : commonProps.defaultValue,
                                                            onChange: handlers.handleTextChange(`${name}.from`),
                                                        } as TextFieldProps)}
                                                        name={`${name}.from`}
                                                        id={`${name}.from`}
                                                        type={'number'}
                                                        placeholder={`${commonProps.placeholder || commonProps.label} From`}
                                                    />
                                                    <span className="input-group-text">to</span>
                                                    <Form.Control
                                                        {...({
                                                            ...commonProps,
                                                            value: value ? value['to'] : commonProps.defaultValue,
                                                            onChange: handlers.handleTextChange(`${name}.to`),
                                                        } as TextFieldProps)}
                                                        name={`${name}.to`}
                                                        id={`${name}.to`}
                                                        type={'number'}
                                                        placeholder={`${commonProps.placeholder || commonProps.label} To`}
                                                    />
                                                </>
                                            );
                                        case 'input-tags':
                                            return (
                                                <>
                                                    <InputTags
                                                        {...({
                                                            ...commonProps,
                                                            values: value,
                                                            onTags: handlers.handleTagChange(
                                                                name,
                                                                (componentProps as TextFieldProps).onChange,
                                                            ),
                                                        } as InputTagsProps)}
                                                    />
                                                    <RequiredField
                                                        required={commonProps.required}
                                                        value={value}
                                                        name={`${name}1`}
                                                        isValid={!!value}
                                                    />
                                                </>
                                            );
                                        case 'image':
                                            return (
                                                <Image
                                                    {...({
                                                        ...commonProps,
                                                        src: value,
                                                    } as ImageFieldProps)}
                                                />
                                            );
                                        case 'custom':
                                            return (
                                                <Component
                                                    {...commonProps}
                                                    value={value}
                                                    values={values}
                                                    onChange={handlers.handleChange}
                                                    onRemoveField={handlers.handleRemoveField(name)}
                                                />
                                            );

                                        default:
                                            return null;
                                    }
                                })()}
                                {commonProps.suffix && (
                                    <InputGroup.Text>
                                        <div dangerouslySetInnerHTML={{ __html: commonProps.suffix }} />
                                    </InputGroup.Text>
                                )}
                                {!groupHelper && helperText && (
                                    <OverlayTrigger
                                        trigger={['hover', 'focus']}
                                        placement="top"
                                        overlay={popoverHoverFocus(helperText, name)}
                                    >
                                        <InputGroup.Text className="help-text">
                                            <i className="fa fas fa-question fa-xs" />
                                        </InputGroup.Text>
                                    </OverlayTrigger>
                                )}
                                <Form.Control.Feedback type="invalid">Please provide a valid {commonProps.label}.</Form.Control.Feedback>
                            </InputGroup>
                            {allowHelperTextWithField ? (
                                helperText ? (
                                    <Form.Text muted>{helperText}</Form.Text>
                                ) : helperComponent ? (
                                    helperComponent
                                ) : null
                            ) : null}
                        </Form.Group>
                    )
                );
            })}
        </>
    ) : null;
});

Fields.displayName = 'Fields';
